import type { MemberAccessEntryTypeOption } from './api';

const TIME = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });
const DAY = new Intl.DateTimeFormat('en-US', { weekday: 'short', day: 'numeric', month: 'short' });

/** "18:05" */
export function timeOf(iso: string | Date): string {
  return TIME.format(new Date(iso));
}

/** "Sat 4 Oct", built from parts because browsers punctuate the joined format differently. */
export function dayOf(iso: string | Date): string {
  const part = Object.fromEntries(DAY.formatToParts(new Date(iso)).map(({ type, value }) => [type, value]));
  return `${part.weekday} ${part.day} ${part.month}`;
}

/** "today 08:12", "yesterday 06:55" or "3 Oct 17:20". */
export function whenOf(iso: string): string {
  const date = new Date(iso);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const day =
    date.toDateString() === new Date().toDateString()
      ? 'today'
      : date.toDateString() === yesterday.toDateString()
        ? 'yesterday'
        : dateOf(date);
  return `${day} ${timeOf(date)}`;
}

/** A PIN's end is exclusive (midnight after the last day), so show the last minute it works. */
export function lastMoment(iso: string): Date {
  return new Date(new Date(iso).getTime() - 1);
}

/** "until Fri 16 Oct", for lists. */
function untilDay(iso: string | null): string {
  return iso ? `until ${dayOf(lastMoment(iso))}` : 'with no end date';
}

/** "until Fri 16 Oct 23:59", where the exact time matters. */
export function untilLabel(iso: string | null): string {
  if (!iso) return 'with no end date';
  const last = lastMoment(iso);
  return `until ${dayOf(last)} ${timeOf(last)}`;
}

/**
 * "until Fri 16 Oct 23:59". A PIN booked ahead says from when, so it isn't tried early: "on Mon 12 Oct
 * until 23:59" for one day, "from Mon 12 Oct until Fri 16 Oct 23:59" for more.
 */
export function validLabel(from: string | null, until: string | null): string {
  if (!from || new Date(from) <= new Date()) return untilLabel(until);
  if (until && lastMoment(until).toDateString() === new Date(from).toDateString()) return `on ${dayOf(from)} until ${timeOf(lastMoment(until))}`;
  return `from ${dayOf(from)} ${untilLabel(until)}`;
}

/** "one entry" or "multiple entries", for the middle of a sentence. */
export function entriesLabel(multipleEntries: boolean): string {
  return multipleEntries ? 'multiple entries' : 'one entry';
}

/** "Valid for the visit day · one entry": the rules every PIN of this entry type follows. */
export function entryTypeRule(type: Pick<MemberAccessEntryTypeOption, 'default_validity_days' | 'allow_multiple_entries'>): string {
  const days = type.default_validity_days === 1 ? 'the visit day' : `${type.default_validity_days} days`;
  return `Valid for ${days} · ${entriesLabel(type.allow_multiple_entries)}`;
}

/** "4 Oct", where the weekday adds nothing. */
export function dateOf(iso: string | Date): string {
  const part = Object.fromEntries(DAY.formatToParts(new Date(iso)).map(({ type, value }) => [type, value]));
  return `${part.day} ${part.month}`;
}

/** "until 23:59" for a PIN that ends today, "until Fri 16 Oct" for a later one. */
export function untilWhen(iso: string | null): string {
  if (!iso) return untilDay(iso);
  const last = lastMoment(iso);
  return last.toDateString() === new Date().toDateString() ? `until ${timeOf(last)}` : untilDay(iso);
}

/** ISO weekdays, Monday first, as the day toggles show them. */
export const WEEKDAYS = [
  { day: 1, label: 'M', short: 'Mon', name: 'Monday' },
  { day: 2, label: 'T', short: 'Tue', name: 'Tuesday' },
  { day: 3, label: 'W', short: 'Wed', name: 'Wednesday' },
  { day: 4, label: 'T', short: 'Thu', name: 'Thursday' },
  { day: 5, label: 'F', short: 'Fri', name: 'Friday' },
  { day: 6, label: 'S', short: 'Sat', name: 'Saturday' },
  { day: 7, label: 'S', short: 'Sun', name: 'Sunday' },
];

/** "Mon, Wed, Fri" for ISO weekdays. */
export function weekdaysLabel(days: number[]): string {
  return WEEKDAYS.filter(({ day }) => days.includes(day)).map(({ short }) => short).join(', ');
}

/** Midnight at the start of today, local time. */
export function startOfToday(): Date {
  const day = new Date();
  day.setHours(0, 0, 0, 0);
  return day;
}

/** "2026-10-21": a calendar day as the API takes it, in local time. */
export function isoDay(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** How far ahead a visit or a booking can be made. */
export const BOOKING_WINDOW_MONTHS = 12;

/** Digits in threes from the right, so "482913" reads "482 913" and "48213" reads "48 213". */
export function groupPin(pin: string): string {
  return pin.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/** "082 555 0142": a South African mobile as people write it; any other number as saved. */
export function displayPhone(phone: string): string {
  const local = phone.replace(/\s/g, '').replace(/^\+27/, '0');
  return /^0\d{9}$/.test(local) ? `${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}` : phone;
}

/** Someone saved with no type of their own: each PIN takes the type picked for it. */
export const ANY_TYPE = { name: 'Any type', rule: 'Use them for any PIN. You pick the type each time.' };

/** A person's type name: Any type when they have none, nothing for one the member can no longer use. */
export function typeNameOf(typeId: string | null, types: MemberAccessEntryTypeOption[] = []): string | undefined {
  return typeId ? types.find((row) => row.access_entry_type_id === typeId)?.name : ANY_TYPE.name;
}

/** "082 555 0142 · Contractor": how to reach a person, then their type. */
export function contactLine(
  person: { phone: string | null; email: string | null; access_entry_type_id: string | null },
  types?: MemberAccessEntryTypeOption[],
): string {
  const reach = person.phone ? displayPhone(person.phone) : person.email;
  return [reach, typeNameOf(person.access_entry_type_id, types)].filter(Boolean).join(' · ');
}
